/* eslint-disable import/no-cycle */
import React, { Suspense, useContext, useEffect, useState } from 'react';
import { setKey } from 'react-geocode';
import { Toaster } from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { BrowserRouter as Router } from 'react-router-dom';
import {
  QueryClientProvider as TanstackClientProvider,
  QueryClient as TanstackQueryClient,
} from '@tanstack/react-query';
import { UserContext } from './Provider/UserProvider';
const AppNavigation = React.lazy(() => import('./navigation/AppNavigation'));
import {
  GoogleGeoCodingKey,
  StripePublishableLiveKey,
  StripePublishableTestKey,
} from './config/secrets';
import ScrollToTop from './components/ScrolltoTop';
import { LocationProvider } from './Provider/LocationProvider';
import { useTranslation } from 'react-i18next';
import { RegionProvider, useRegion } from './Provider/RegionProvider';
import HreflangTags from './common/HreflangTags';
import CanonicalTag from './common/CanonicalTag';
import ReactGA from 'react-ga4';
import { logPageView } from './util/analytics';
import PageViewTracker from './util/PageViewTracker';
import GeoRegionPrompt from './home/components/GeoRegionPrompt';
// import NotificationListener from './notifications/components/NotificationListener';
export const tanstackClient = new TanstackQueryClient();

function SeoMetaUpdater() {
  const { i18n } = useTranslation();
  const { country } = useRegion();

  useEffect(() => {
    const seoLanguage =
      country?.code === 'MX' || country?.code === 'PE' ? 'es' : 'en';
    const description = i18n.getFixedT(seoLanguage)('seo.metaDescription');
    const metaDescription = document.querySelector(
      'meta[name="description"]',
    );

    // A page that writes its own description (blog posts, via Helmet) owns it.
    // Overwriting it here with the generic marketplace copy is what left every
    // article sharing the site-wide description in the rendered DOM.
    const pageOwnsDescription =
      metaDescription?.hasAttribute('data-react-helmet');

    if (metaDescription && !pageOwnsDescription) {
      metaDescription.setAttribute('content', description);
    }

    document.documentElement.lang = seoLanguage;
  }, [country?.code, i18n]);

  return null;
}

/* =============================================================================
<App />
============================================================================= */

function App() {
  const { user } = useContext(UserContext);
  const [stripePromise, setStripePromise] = useState(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (GoogleGeoCodingKey) {
      try {
        setKey(GoogleGeoCodingKey);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('geocode setKey failed', err);
      }
    }
  }, []);
  useEffect(() => {
    if (!user) return;
    const publishableKey =
      user.paymentEnv === 'test'
        ? StripePublishableTestKey
        : StripePublishableLiveKey;

    // console.log(
    //   'Initialising Stripe with env:',
    //   user.paymentEnv,
    //   publishableKey,
    // );
    const initializeStripe = async () => {
      const loadStripePromise = await loadStripe(publishableKey, {
        locale: i18n.language,
      })
      setStripePromise(loadStripePromise);
    };
    initializeStripe();
  }, [user, user?.paymentEnv]);
useEffect(() => {
  const hostname = window.location.hostname;
  const trackingId = hostname.includes('carsnus.pe')
    ? 'G-DWMZ8T0R02'
    : 'G-61823HHFMC'; // default: carsnus.com
  ReactGA.initialize(trackingId);
}, []);
  return (
    <RegionProvider>
      <LocationProvider>
        <Elements stripe={stripePromise}>
          <Router>
            <SeoMetaUpdater />
            <CanonicalTag />
            <HreflangTags/>
            <ScrollToTop />
             <PageViewTracker />
            <GeoRegionPrompt />
            <Suspense
              fallback={
                <div className='min-h-screen flex items-center justify-center'>
                  {t('common.loading')}...
                </div>
              }
            >
              <AppNavigation />
            </Suspense>
            {/* <NotificationListener /> */}
          </Router>
        </Elements>
        <Toaster
          position='top-right'
          toastOptions={{
            duration: 3000,
          }}
        />
      </LocationProvider>
    </RegionProvider>
  );
}

/* Export
============================================================================= */
export default App;
